import React, { useEffect, useState } from "react";

const API_BASE = "https://gantasoft.in/cab-g/dev/admin"; // change to your API base

const QUICK_RANGES = [
  { key: "today", label: "Today" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "year", label: "Year" },
  { key: "between", label: "Between" },
];

export default function AdminRides() {
  // filters
  const [period, setPeriod] = useState("today");
  const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [vehicle, setVehicle] = useState(""); // e.g. bike/auto/cab
  const [status, setStatus] = useState("");   // pending/accepted/started/completed/cancelled/rejected

  // paging + sorting
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sort, setSort] = useState("created_at:desc"); // id|created_at|fare|distance_km|status

  // data
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const fetchRides = async (resetPage = false) => {
    if (resetPage) setPage(1);
    setLoading(true);
    setErr("");
    try {
      const qs = new URLSearchParams({
        period,
        page: String(resetPage ? 1 : page),
        limit: String(limit),
        sort,
        tz: "Asia/Kolkata",
      });
      if (period === "between") {
        qs.set("from", from);
        qs.set("to", to);
      }
      if (vehicle) qs.set("vehicle_type", vehicle);
      if (status) qs.set("status", status);

      const res = await fetch(`${API_BASE}/rides_list.php?${qs.toString()}`);
      const j = await res.json();
      if (!j.success) throw new Error(j.message || "Failed to fetch");
      setData(j);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRides(true); }, []);           // initial
  useEffect(() => { fetchRides(false); }, [page, sort]); // page/sort changes

  const rows = data?.rows || [];
  const pages = data?.pages || 1;

  const setRange = (key) => {
    setPeriod(key);
    if (key !== "between") {
      const today = new Date().toISOString().slice(0, 10);
      setFrom(today);
      setTo(today);
    }
    fetchRides(true);
  };

  const toggleSort = (field) => {
    const [f, dir] = sort.split(":");
    if (f === field) {
      setSort(`${field}:${dir === "asc" ? "desc" : "asc"}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-4">
      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-6 flex gap-2 flex-wrap">
          {QUICK_RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 rounded-full border text-sm ${
                period === r.key ? "bg-black text-white" : "bg-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {period === "between" && (
          <>
            <div className="md:col-span-3">
              <div className="text-xs font-semibold">From</div>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="border rounded-md px-2 py-1 w-full"
              />
            </div>
            <div className="md:col-span-3">
              <div className="text-xs font-semibold">To</div>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="border rounded-md px-2 py-1 w-full"
              />
            </div>
          </>
        )}

        <div className="md:col-span-3">
          <div className="text-xs font-semibold">Vehicle Type</div>
          <input
            value={vehicle}
            onChange={(e) => setVehicle(e.target.value)}
            placeholder="bike / auto / cab"
            className="border rounded-md px-2 py-1 w-full"
          />
        </div>

        <div className="md:col-span-3">
          <div className="text-xs font-semibold">Status</div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border rounded-md px-2 py-1 w-full"
          >
            <option value="">All</option>
            <option value="pending">pending</option>
            <option value="accepted">accepted</option>
            <option value="started">started</option>
            <option value="completed">completed</option>
            <option value="cancelled">cancelled</option>
            <option value="rejected">rejected</option>
          </select>
        </div>

        <div className="md:col-span-12 flex gap-2">
          <button
            onClick={() => fetchRides(true)}
            className="px-3 py-2 rounded-md bg-black text-white"
          >
            Apply
          </button>
          <button
            onClick={() => {
              setVehicle("");
              setStatus("");
              setPeriod("today");
              const today = new Date().toISOString().slice(0, 10);
              setFrom(today);
              setTo(today);
              setPage(1);
              setSort("created_at:desc");
              fetchRides(true);
            }}
            className="px-3 py-2 rounded-md border"
          >
            Reset
          </button>
          {loading && <span className="text-sm">Loading…</span>}
          {err && <span className="text-sm text-red-600">{err}</span>}
        </div>
      </div>

      {/* Aggregates */}
      {data?.aggregate && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="bg-white p-3 rounded-xl shadow-sm">
            <div className="text-xs text-gray-500">Rides</div>
            <div className="text-xl font-bold">{data.aggregate.rides}</div>
          </div>
          <div className="bg-white p-3 rounded-xl shadow-sm">
            <div className="text-xs text-gray-500">Completed</div>
            <div className="text-xl font-bold">{data.aggregate.completed}</div>
          </div>
          <div className="bg-white p-3 rounded-xl shadow-sm">
            <div className="text-xs text-gray-500">Fare (₹)</div>
            <div className="text-xl font-bold">{data.aggregate.fare_total}</div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white p-4 rounded-xl shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                {[
                  ["id", "ID"],
                  ["created_at", "CREATED AT"],
                  ["driver_id", "DRIVER"],
                  ["rider_id", "RIDER"],
                  ["status", "STATUS"],
                  ["vehicle_type", "VEHICLE"],
                  ["fare", "FARE (₹)"],
                  ["tip", "TIP (₹)"],
                  ["payment_mode", "PAY MODE"],
                  ["pickup_address", "PICKUP"],
                  ["drop_address", "DROP"],
                ].map(([field, label]) => (
                  <th
                    key={field}
                    className="py-2 cursor-pointer select-none"
                    onClick={() => toggleSort(field)}
                    title={`Sort by ${field}`}
                  >
                    {label}
                    {sort.startsWith(field + ":") && (
                      <span className="ml-1 text-xs">
                        {sort.endsWith(":asc") ? "▲" : "▼"}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="py-2">#{r.id}</td>
                  <td>{new Date((r.created_at || "").replace(" ", "T")).toLocaleString()}</td>
                  <td>{r.driver_id ?? "-"}</td>
                  <td>{r.rider_id}</td>
                  <td>
                    <span className="px-2 py-0.5 rounded-full bg-gray-100">
                      {r.status}
                    </span>
                  </td>
                  <td>{r.vehicle_type}</td>
                  <td>₹{Number(r.fare ?? 0).toFixed(2)}</td>
                  <td>₹{Number(r.tip ?? 0).toFixed(2)}</td>
                  <td>{r.payment_mode || "-"}</td>
                  <td title={r.pickup_address}>{r.pickup_address?.slice(0, 22) || "-"}</td>
                  <td title={r.drop_address}>{r.drop_address?.slice(0, 22) || "-"}</td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={11} className="py-6 text-center text-gray-500">
                    No rides found for this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-3">
          <div className="text-xs text-gray-500">
            Page {data?.page || 1} / {pages}
          </div>
          <div className="flex gap-2">
            <button
              disabled={(data?.page || 1) <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1 rounded-md border disabled:opacity-50"
            >
              Prev
            </button>
            <button
              disabled={(data?.page || 1) >= pages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 rounded-md border disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div>
            <select
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
                fetchRides(true);
              }}
              className="border rounded-md px-2 py-1 text-sm"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
